/**
 * InfoTooltip - Wiederverwendbare Tooltip-Komponente mit Help-Cursor
 * Verwendet für informative Tooltips, die Erklärungen oder Berechnungsformeln anzeigen
 */
import React from "react";
import { Box, Tooltip, TooltipProps, Typography } from "@mui/material";
import { colors, fonts } from "../../../styles/theme";

interface InfoTooltipProps extends Omit<TooltipProps, "children"> {
  /** Tooltip-Text oder React-Element */
  title: React.ReactNode;
  /** Optionale Überschrift, wird über dem Inhalt angezeigt */
  heading?: React.ReactNode;
  /** Kinder-Element(e) - wird in span gewrappt für korrektes Cursor-Verhalten */
  children: React.ReactNode;
  /** Zeigt Help-Cursor (Standard: true) */
  showHelpCursor?: boolean;
}

/**
 * Inhalt eines erklärenden Tooltips mit Überschrift: oben halbfett, was es ist („Istkosten“,
 * „Bearbeitungsweg: Beauftragen“), darunter in ein, zwei Zeilen, was es bedeutet. `InfoTooltip`
 * setzt ihn über `heading` selbst; einzeln gebraucht wird er, wo ein anderer Tooltip den Titel
 * zeigt, etwa an den Optionen eines `Dropdown`.
 *
 * Aus Spans, damit er auch in einem Span als Titel stehen darf.
 */
export const InfoTooltipContent: React.FC<{
  heading: React.ReactNode;
  children: React.ReactNode;
}> = ({ heading, children }) => (
  <Box component="span" sx={{ display: "block" }}>
    <Typography
      component="span"
      variant="body2"
      sx={{
        display: "block",
        fontWeight: fonts.weights.semiBold,
        color: colors.tooltip.heading,
        mb: 0.5,
      }}
    >
      {heading}
    </Typography>
    <Typography
      component="span"
      variant="body2"
      sx={{ display: "block", color: colors.tooltip.text }}
    >
      {children}
    </Typography>
  </Box>
);

/**
 *
 * Features:
 * - Automatischer Help-Cursor (?) beim Hover
 * - Standardmäßig mit Pfeil und Platzierung rechts (über `placement` änderbar: top/bottom/left/right)
 * - Optionale Überschrift über dem Inhalt
 * - Wrappt Kinder automatisch in span für korrektes Event-Handling
 *
 * Verwendung:
 * ```tsx
 * <InfoTooltip heading="Berechnung" title="Formel: X × Y" placement="top">
 *   <span>Label mit Erklärung</span>
 * </InfoTooltip>
 * ```
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  heading,
  children,
  showHelpCursor = true,
  arrow = true,
  placement = "right",
  ...tooltipProps
}) => {
  const content = heading ? (
    <InfoTooltipContent heading={heading}>{title}</InfoTooltipContent>
  ) : (
    // Bewusst in ein Element gewrappt statt als reiner String: MUI hängt einen String-Titel
    // bei describeChild zusätzlich als natives title-Attribut ans Kind, was einen zweiten,
    // browsereigenen Tooltip neben unserem erzeugen würde. Ein span ändert die Darstellung
    // nicht, unterbindet aber diesen Fallback.
    <span>{title}</span>
  );

  return (
    <Tooltip
      title={content}
      arrow={arrow}
      placement={placement}
      // Der Tooltip beschreibt sein Kind, er benennt es nicht: ohne describeChild setzt MUI
      // den Tooltip-Text als aria-label und überschreibt damit den eigentlichen Namen des
      // Elements. Bei Formularfeldern (Dropdown reicht sein Label als Tooltip-Kind durch)
      // hieß das Feld dann nicht mehr "Windenergiegebiet", sondern trug den ganzen
      // Erklärtext als Namen — Screenreader lasen das vor, und Tests fanden das Feld nicht
      // mehr eindeutig.
      describeChild
      {...tooltipProps}
    >
      <span
        style={{
          cursor: showHelpCursor ? "help" : "inherit",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {children}
      </span>
    </Tooltip>
  );
};

export default InfoTooltip;
